<template>
  <div class="flashcard-view">
    <!-- Session Not Started State -->
    <div v-if="!sessionStarted" class="session-start-container">
      <h1>Card Review</h1>
      <div class="start-session-content">
        <p class="start-session-description">Ready to review your flashcards?</p>
        <button @click="startSession" class="start-session-button">
          Start Session
        </button>
      </div>
    </div>

    <!-- Session Active State -->
    <template v-else>
      <div class="session-header">
        <h1>Card Review</h1>
        <div class="session-stats">
          <div class="stat-box">
            <span class="stat-label">Time:</span>
            <span class="stat-value">{{ formatSessionTime(sessionTime) }}</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">Reviewed:</span>
            <span class="stat-value">{{ cardsReviewedThisSession }}</span>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="loading-message">
        <p>Loading next card...</p>
      </div>

      <div v-if="!isLoading && !currentCard && !allCardsDone" class="no-cards-message">
        <p>No cards due for review right now. Check back later!</p>
      </div>

      <div v-if="allCardsDone" class="all-done-message">
        <p>Congratulations! You've reviewed all available cards for now!</p>
      </div>

    <div v-if="!isLoading && currentCard" class="flashcard-container">
      <div class="card-header">
        <div class="card-header-left">
          <span class="card-type-badge">Card</span>
          <span class="card-csv-number">#{{ currentCard.card_id }}</span>
          <span v-if="currentCard.total_reviews > 0" class="card-review-count">
            ({{ currentCard.total_reviews }} review{{ currentCard.total_reviews !== 1 ? 's' : '' }})
          </span>
          <span v-else class="card-review-count">Never reviewed</span>
        </div>
        <div class="card-actions">
          <button @click="startEdit" class="secondary-button" type="button">Edit Card</button>
        </div>
      </div>

      <div v-if="isEditing" class="card-edit">
        <div class="edit-field">
          <label for="editFront">Front</label>
          <textarea id="editFront" v-model="editFront" rows="2"></textarea>
        </div>
        <div class="edit-field">
          <label for="editBack">Back</label>
          <textarea id="editBack" v-model="editBack" rows="2"></textarea>
        </div>
        <div class="edit-field">
          <label for="editNotes">Notes</label>
          <textarea id="editNotes" v-model="editNotes" rows="3"></textarea>
        </div>
        <div class="edit-actions">
          <button @click="saveEdit" :disabled="isSavingEdit" class="action-button" type="button">
            {{ isSavingEdit ? 'Saving...' : 'Save' }}
          </button>
          <button @click="cancelEdit" :disabled="isSavingEdit" class="secondary-button" type="button">Cancel</button>
        </div>
        <p v-if="editError" class="error-text">{{ editError }}</p>
      </div>

      <!-- Front of the Card -->
      <div class="card-front">
        <p class="sentence-display" style="white-space: pre-wrap;">{{ currentCard.front }}</p>
        <p v-if="currentCard.last_reviewed_date" class="last-reviewed">
          Last reviewed: {{ formatDate(currentCard.last_reviewed_date) }}
        </p>
      </div>

      <div class="review-inputs">
        <div>
          <label for="typedInput">Type your answer (optional):</label>
          <textarea id="typedInput" v-model="typedInput" rows="1" class="typed-input"></textarea>
        </div>
      </div>

      <!-- Answer Button -->
      <button v-if="!showAnswer" @click="revealAnswer" class="action-button">Show Answer</button>

      <div v-if="showAnswer">
        <div class="review-inputs">
          <button @click="submitReviewAndNext" :disabled="isSubmitting" class="action-button" data-testid="submit-review-button">
            {{ isSubmitting ? 'Submitting...' : 'Next Card' }}
          </button>
        </div>

        <div class="card-back">
          <hr>
          <p style="white-space: pre-wrap;"><strong>Answer:</strong> {{ currentCard.back }}</p>

          <div v-if="currentCard.notes" class="comments-section">
            <strong>Notes:</strong>
            <p style="white-space: pre-wrap;">{{ currentCard.notes }}</p>
          </div>
        </div>
        <div class="review-inputs">
          <div>
            <label for="userScore">Your Score (0.0 - 1.0):</label>
            <div class="score-quick-picks">
              <button
                v-for="score in scoreShortcuts"
                :key="score"
                @click="setScore(score)"
                :class="['score-button', { 'score-button-active': userScore === score }]"
                type="button"
              >
                {{ score }}
              </button>
            </div>
            <input type="number" id="userScore" v-model.number="userScore" min="0" max="1" step="0.1" class="score-input" />
          </div>
          <div>
            <label for="userComment">Your Comment (optional):</label>
            <textarea id="userComment" v-model="userComment" rows="3"></textarea>
          </div>
        </div>

        <!-- Review History -->
        <div v-if="isLoadingReviews" class="loading-reviews">
          Loading previous reviews...
        </div>
        <p v-else-if="reviewLoadError" class="error-text">{{ reviewLoadError }}</p>
        <div v-if="recentReviews.length > 0" class="review-history">
          <h3>Previous Reviews</h3>
          <div class="review-history-table">
            <div class="review-row review-header">
              <span class="review-col">Date</span>
              <span class="review-col">Score</span>
              <span class="review-col">Interval</span>
              <span class="review-col">Ease Factor</span>
            </div>
            <div v-for="review in recentReviews" :key="review.review_id" class="review-row">
              <span class="review-col">{{ formatDate(review.review_timestamp) }}</span>
              <span class="review-col" :class="getScoreClass(review.user_score)">
                {{ (review.user_score * 100).toFixed(0) }}%
              </span>
              <span class="review-col">{{ review.interval_at_review }} day{{ review.interval_at_review !== 1 ? 's' : '' }}</span>
              <span class="review-col">{{ review.ease_factor_at_review.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <div class="card-stats">
          <div class="stat-item">
            <strong>Status:</strong>
            <span :class="currentCard.is_learning ? 'status-learning' : 'status-review'">
              {{ currentCard.is_learning ? 'Learning' : 'Review' }}
            </span>
          </div>
          <div class="stat-item">
            <strong>Reviews:</strong>
            <span v-if="currentCard.total_reviews > 0">{{ currentCard.total_reviews }}</span>
            <span v-else>Never reviewed</span>
            <span v-if="currentCard.total_reviews > 0 && currentCard.average_score !== null" class="avg-score">
              (Avg: {{ (currentCard.average_score * 100).toFixed(0) }}%)
            </span>
          </div>
          <div class="stat-item" v-if="currentCard.next_review_date">
            <strong>Next review:</strong> {{ formatDate(currentCard.next_review_date) }}
            <span v-if="getDaysUntilReview(currentCard.next_review_date) !== null" class="days-until">
              ({{ getDaysUntilReview(currentCard.next_review_date) }})
            </span>
          </div>
          <div class="stat-item ease-factor-info">
            <strong>Ease Factor:</strong> {{ currentCard.ease_factor ? currentCard.ease_factor.toFixed(2) : '2.50' }}
            <span
              class="info-icon"
              data-tooltip="Ease Factor: controls how fast the interval grows. Higher = longer gaps after good scores; lower = shorter gaps after weak scores."
              tabindex="0"
              role="img"
              aria-label="Ease Factor help"
            >ℹ️</span>
          </div>
          <div class="stat-item interval-info" v-if="currentCard.interval_days !== null && currentCard.interval_days !== undefined">
            <strong>Interval:</strong> {{ currentCard.interval_days }} day{{ currentCard.interval_days !== 1 ? 's' : '' }}
            <span
              class="info-icon"
              data-tooltip="Interval: days between reviews. If interval is 4 and you review today, the card is due again in 4 calendar days."
              role="img"
              aria-label="Interval help"
              tabindex="0"
            >ℹ️</span>
          </div>
        </div>

      </div>
    </div>

    <div v-if="errorMessage && !isLoading" class="error-message central-error">
      <p>{{ errorMessage }}</p>
    </div>
    </template>
  </div>
</template>

<script>
import ApiService from '@/services/ApiService';

export default {
  name: 'CardFlashcardView',
  data() {
    return {
      currentCard: null,
      showAnswer: false,
      typedInput: '',
      userScore: 1.0,
      userComment: '',
      isEditing: false,
      isSavingEdit: false,
      editFront: '',
      editBack: '',
      editNotes: '',
      editError: '',
      isLoading: false,
      isSubmitting: false,
      allCardsDone: false,
      errorMessage: '',
      scoreShortcuts: [0, 0.3, 0.5, 0.7, 0.8, 0.9, 1.0],
      sessionStartTime: null,
      sessionTime: 0,
      sessionTimer: null,
      cardsReviewedThisSession: 0,
      currentSessionId: null,
      sessionStarted: false,
      recentReviews: [],
      isLoadingReviews: false,
      reviewsLoaded: false,
      reviewLoadError: '',
      sessionAccumulatedMs: 0,
      sessionLastActiveAt: null
    };
  },
  methods: {
    async startSession() {
      this.sessionStarted = true;
      this.isLoading = true;
      await this.startSessionTimer();
      await this.fetchNextCard();
    },
    async fetchNextCard() {
      this.isLoading = true;
      this.errorMessage = '';
      this.allCardsDone = false;
      this.recentReviews = []; // Clear previous reviews
      this.isLoadingReviews = false;
      this.reviewsLoaded = false;
      this.reviewLoadError = '';
      try {
        const response = await ApiService.getNextCardV2();
        if (response.status === 200 && response.data) {
          this.currentCard = response.data;
          this.showAnswer = false;
          this.typedInput = '';
          this.userScore = 1.0;
          this.userComment = '';
          this.isEditing = false;
          this.isSavingEdit = false;
          this.editError = '';
        } else if (response.status === 204) {
          this.currentCard = null;
          this.allCardsDone = true;
        }
      } catch (error) {
        console.error("Error fetching next card:", error);
        this.errorMessage = 'Failed to load the next card. Please check your connection or try again later.';
        if (error.response && error.response.status === 204) {
          this.currentCard = null;
          this.allCardsDone = true;
        } else {
          this.currentCard = null;
        }
      } finally {
        this.isLoading = false;
      }
    },
    revealAnswer() {
      this.showAnswer = true;
      this.loadRecentReviews();
    },
    setScore(score) {
      this.userScore = score;
    },
    handleKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        if (this.showAnswer) {
          if (!this.isSubmitting) {
            this.submitReviewAndNext();
          }
        } else {
          this.revealAnswer();
        }
        return;
      }
      if (!this.showAnswer || event.key !== '0') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const tagName = event.target && event.target.tagName ? event.target.tagName.toLowerCase() : '';
      if (tagName === 'input' || tagName === 'textarea') return;
      this.setScore(0);
    },
    async loadRecentReviews() {
      if (!this.currentCard || this.reviewsLoaded || this.isLoadingReviews) return;
      this.isLoadingReviews = true;
      this.reviewLoadError = '';
      try {
        const response = await ApiService.getCardDetails(this.currentCard.card_id);
        if (response && response.data && response.data.reviews) {
          this.recentReviews = response.data.reviews.slice(0, 10);
        } else {
          this.recentReviews = [];
        }
        this.reviewsLoaded = true;
      } catch (error) {
        console.error('Error loading reviews:', error);
        this.reviewLoadError = 'Failed to load previous reviews. Please try again.';
      } finally {
        this.isLoadingReviews = false;
      }
    },
    startEdit() {
      if (!this.currentCard) return;
      this.editFront = this.currentCard.front || '';
      this.editBack = this.currentCard.back || '';
      this.editNotes = this.currentCard.notes || '';
      this.editError = '';
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editError = '';
    },
    async saveEdit() {
      if (!this.currentCard) return;
      this.isSavingEdit = true;
      this.editError = '';
      try {
        const payload = {
          front: this.editFront,
          back: this.editBack,
          notes: this.editNotes
        };
        const response = await ApiService.updateCard(this.currentCard.card_id, payload);
        if (response && response.data) {
          this.currentCard = response.data;
        } else {
          this.currentCard.front = this.editFront;
          this.currentCard.back = this.editBack;
          this.currentCard.notes = this.editNotes;
        }
        this.isEditing = false;
      } catch (error) {
        console.error('Error updating card:', error);
        this.editError = 'Failed to save changes. Please try again.';
      } finally {
        this.isSavingEdit = false;
      }
    },
    async submitReviewAndNext() {
      if (this.userScore === null || this.userScore < 0 || this.userScore > 1) {
        this.errorMessage = "Please enter a score between 0.0 and 1.0.";
        return;
      }
      this.isSubmitting = true;
      this.errorMessage = '';
      try {
        const response = await ApiService.submitCardReview(
          this.currentCard.card_id,
          this.userScore,
          this.userComment,
          this.typedInput,
          this.currentSessionId
        );
        // Capture recent reviews from the response
        if (response.data && response.data.recent_reviews) {
          this.recentReviews = response.data.recent_reviews;
        }
        this.reviewsLoaded = true;
        this.reviewLoadError = '';
        // Increment review counter
        this.cardsReviewedThisSession++;
        await this.fetchNextCard();
      } catch (error) {
        console.error("Error submitting review:", error);
        this.errorMessage = 'Failed to submit your review. Please try again.';
      } finally {
        this.isSubmitting = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    getScoreClass(score) {
      if (score >= 0.9) return 'score-excellent';
      if (score >= 0.8) return 'score-good';
      if (score >= 0.6) return 'score-pass';
      return 'score-fail';
    },
    getDaysUntilReview(dateString) {
      if (!dateString) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const reviewDate = new Date(dateString);
      reviewDate.setHours(0, 0, 0, 0);
      const diffTime = reviewDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return 'overdue';
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'tomorrow';
      return `in ${diffDays} days`;
    },
    formatSessionTime(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    },
    async startSessionTimer() {
      this.sessionStartTime = Date.now();
      this.sessionAccumulatedMs = 0;
      this.sessionLastActiveAt = document.hidden ? null : Date.now();
      // Start a study session in the backend
      try {
        const response = await ApiService.startStudySession();
        if (response.status === 201 && response.data) {
          this.currentSessionId = response.data.session_id;
        }
      } catch (error) {
        console.warn('Failed to start study session:', error);
        // Continue without session tracking
      }
      this.sessionTimer = setInterval(() => {
        const activeElapsed = this.sessionLastActiveAt
          ? Date.now() - this.sessionLastActiveAt
          : 0;
        const elapsed = Math.floor((this.sessionAccumulatedMs + activeElapsed) / 1000);
        this.sessionTime = elapsed;
      }, 1000);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    handleVisibilityChange() {
      if (!this.sessionStarted) return;
      if (document.hidden) {
        this.pauseSessionTimer();
      } else {
        this.resumeSessionTimer();
      }
    },
    pauseSessionTimer() {
      if (this.sessionLastActiveAt) {
        this.sessionAccumulatedMs += Date.now() - this.sessionLastActiveAt;
        this.sessionLastActiveAt = null;
      }
    },
    resumeSessionTimer() {
      if (!this.sessionLastActiveAt) {
        this.sessionLastActiveAt = Date.now();
      }
    },
    async stopSessionTimer() {
      if (this.sessionTimer) {
        clearInterval(this.sessionTimer);
        this.sessionTimer = null;
      }
      this.pauseSessionTimer();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      // End the study session in the backend
      if (this.currentSessionId) {
        try {
          await ApiService.endStudySession(this.currentSessionId);
        } catch (error) {
          console.warn('Failed to end study session:', error);
        }
        this.currentSessionId = null;
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
    // Session is no longer started automatically - user must click "Start Session"
  },
  beforeUnmount() {
    this.stopSessionTimer();
    window.removeEventListener('keydown', this.handleKeydown);
  },
};
</script>

<style scoped>
.flashcard-view {
  padding: 12px;
  max-width: 700px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.session-header h1 {
  margin: 0;
  font-size: 1.4em;
}

.session-stats {
  display: flex;
  gap: 20px;
}

.stat-box {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  background-color: var(--bg-secondary);
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.stat-label {
  font-size: 0.7em;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.stat-value {
  font-size: 1em;
  font-weight: bold;
  color: var(--button-primary);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color);
}

.card-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-type-badge {
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 0.8em;
}

.card-csv-number {
  font-size: 0.9em;
  color: var(--text-secondary);
}

.card-review-count {
  font-size: 0.85em;
  color: var(--text-tertiary);
}
.card-actions {
  margin-left: auto;
}

.loading-message, .no-cards-message, .all-done-message, .error-message {
  text-align: center;
  padding: 20px;
  background-color: var(--bg-secondary);
  border-radius: 8px;
  margin-top: 20px;
  color: var(--text-primary);
}

.error-message {
  color: var(--error-color);
  background-color: var(--error-bg);
}

.central-error {
  margin-top: 20px;
  text-align: center;
  padding: 15px;
  border-radius: 8px;
  color: var(--text-primary);
}

.flashcard-container {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 14px;
  background-color: var(--card-bg);
  box-shadow: 0 2px 4px var(--shadow);
  color: var(--text-primary);
}

.sentence-display {
  font-size: 1.2em;
  color: var(--text-primary);
  margin-bottom: 15px;
  line-height: 1.5;
}

.last-reviewed {
  font-size: 0.8em;
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.action-button {
  background-color: #4CAF50;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  margin-top: 10px;
  transition: background-color 0.3s;
}

.action-button:hover {
  background-color: #45a049;
}

.action-button:disabled {
  background-color: var(--bg-tertiary);
  cursor: not-allowed;
}

.secondary-button {
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  padding: 8px 14px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.3s;
}

.secondary-button:hover {
  background-color: var(--bg-secondary);
}

.card-edit {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 15px;
  background-color: var(--bg-secondary);
}

.edit-field {
  margin-bottom: 10px;
}

.edit-field label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: var(--text-primary);
}

.edit-field textarea {
  width: calc(100% - 22px);
  padding: 10px;
  border: 1px solid var(--input-border);
  border-radius: 4px;
  font-size: 1em;
  background-color: var(--input-bg);
  color: var(--text-primary);
}

.edit-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.error-text {
  margin-top: 10px;
  color: var(--error-color);
  font-size: 0.9em;
}

.card-back {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color);
}

.comments-section {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 10px;
  margin-top: 15px;
  border-radius: 4px;
  font-size: 0.9em;
  color: var(--text-primary);
}

.comments-section strong {
  display: block;
  margin-bottom: 5px;
  color: var(--text-primary);
}

.review-inputs {
  margin-top: 12px;
}

.review-inputs div {
  margin-bottom: 8px;
}

.review-inputs label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: var(--text-primary);
}

.review-inputs input[type="number"],
.review-inputs textarea {
  width: calc(100% - 22px);
  padding: 10px;
  border: 1px solid var(--input-border);
  border-radius: 4px;
  font-size: 1em;
  background-color: var(--input-bg);
  color: var(--text-primary);
}

.review-inputs textarea {
  resize: vertical;
  min-height: 60px;
}

.review-inputs textarea.typed-input {
  min-height: 30px;
  height: 30px;
  padding: 6px 10px;
  resize: none;
}

.score-quick-picks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  justify-content: center;
}

.score-button {
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  border: 2px solid var(--border-color);
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.95em;
  font-weight: 500;
  transition: all 0.2s;
  min-width: 50px;
}

.score-button:hover {
  background-color: var(--bg-secondary);
  border-color: var(--accent-color);
}

.score-button-active {
  background-color: #4CAF50;
  color: white;
  border-color: #4CAF50;
}

.score-button-active:hover {
  background-color: #45a049;
  border-color: #45a049;
}

.score-input {
  margin-top: 8px;
}

.card-stats {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 12px;
  margin-top: 10px;
  font-size: 0.9em;
  color: var(--text-primary);
}

.stat-item {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-item:last-child {
  margin-bottom: 0;
}

.stat-item strong {
  color: var(--text-primary);
  min-width: 100px;
}

.status-learning {
  background-color: #ffc107;
  color: #000;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: bold;
}

.status-review {
  background-color: #28a745;
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: bold;
}

.avg-score {
  color: #666;
  font-size: 0.9em;
  margin-left: 5px;
}

.days-until {
  color: #17a2b8;
  font-size: 0.9em;
  margin-left: 5px;
  font-weight: 500;
}

.ease-factor-info {
  position: relative;
}

.info-icon {
  cursor: help;
  margin-left: 5px;
  font-size: 0.9em;
  opacity: 0.7;
  position: relative;
  display: inline-flex;
  align-items: center;
}

.info-icon[data-tooltip]:hover::after,
.info-icon[data-tooltip]:focus::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 130%;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.85em;
  max-width: 240px;
  white-space: normal;
  box-shadow: 0 2px 6px var(--shadow);
  z-index: 10;
}

.ease-factor-info:hover .info-icon {
  opacity: 1;
}

.interval-info {
  position: relative;
}

.interval-info .info-icon {
  cursor: help;
  margin-left: 5px;
  font-size: 0.9em;
  opacity: 0.7;
}

.interval-info:hover .info-icon {
  opacity: 1;
}

/* Review History Styles */
.loading-reviews {
  margin-top: 10px;
  color: var(--text-secondary);
  font-size: 0.95em;
}

.review-history {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 2px solid var(--border-color);
}

.review-history h3 {
  margin-bottom: 15px;
  color: var(--text-primary);
  font-size: 1.1em;
}

.review-history-table {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  overflow: hidden;
}

.review-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 10px;
  padding: 10px 15px;
  border-bottom: 1px solid var(--border-color);
}

.review-row:last-child {
  border-bottom: none;
}

.review-header {
  background-color: var(--bg-tertiary);
  font-weight: bold;
  color: var(--text-primary);
}

.review-col {
  color: var(--text-primary);
}

.score-excellent {
  color: #28a745;
  font-weight: bold;
}

.score-good {
  color: #4CAF50;
  font-weight: bold;
}

.score-pass {
  color: #ffc107;
  font-weight: bold;
}

.score-fail {
  color: #dc3545;
  font-weight: bold;
}

.session-start-container {
  text-align: center;
  padding: 40px 20px;
}

.session-start-container h1 {
  margin-bottom: 30px;
  color: var(--text-primary);
}

.start-session-content {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 40px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 4px 6px var(--shadow);
}

.start-session-description {
  font-size: 1.1em;
  color: var(--text-secondary);
  margin-bottom: 25px;
}

.start-session-button {
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px 40px;
  font-size: 1.2em;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.1s;
}

.start-session-button:hover {
  background-color: #45a049;
  transform: translateY(-2px);
}

.start-session-button:active {
  transform: translateY(0);
}

/* Review History Styles */
.loading-reviews {
  margin-top: 10px;
  color: var(--text-secondary);
  font-size: 0.95em;
}

.review-history {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 2px solid var(--border-color);
}

.review-history h3 {
  margin-bottom: 15px;
  color: var(--text-primary);
  font-size: 1.1em;
}

.review-history-table {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  overflow: hidden;
}

.review-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 10px;
  padding: 10px 15px;
  border-bottom: 1px solid var(--border-color);
}

.review-row:last-child {
  border-bottom: none;
}

.review-header {
  background-color: var(--bg-tertiary);
  font-weight: bold;
  color: var(--text-primary);
}

.review-col {
  color: var(--text-primary);
}

.score-excellent {
  color: #28a745;
  font-weight: bold;
}

.score-good {
  color: #4CAF50;
  font-weight: bold;
}

.score-pass {
  color: #ffc107;
  font-weight: bold;
}

.score-fail {
  color: #dc3545;
  font-weight: bold;
}
.session-start-container {
  text-align: center;
  padding: 40px 20px;
}

.session-start-container h1 {
  margin-bottom: 30px;
  color: var(--text-primary);
}

.start-session-content {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 40px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 4px 6px var(--shadow);
}

.start-session-description {
  font-size: 1.1em;
  color: var(--text-secondary);
  margin-bottom: 25px;
}

.start-session-button {
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px 40px;
  font-size: 1.2em;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.1s;
}

.start-session-button:hover {
  background-color: #45a049;
  transform: translateY(-2px);
}

.start-session-button:active {
  transform: translateY(0);
}
</style>
